import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon, GroupIcon, PhoneIcon, ConferenceIcon, MoreIcon } from "../../../svg/chat";
import Moment from "react-moment";
import CallHistory from "../CallHistory";
import "./style.css";

export default function ChatSidebar({
  conversations,
  activeConversation,
  onlineUsers,
  typingUsers,
  onSelectConversation,
  onOpenConference,
  visible,
  user,
  onCallUser,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("chats");
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm) return true;

    if (conv.type === "group") {
      return conv.group?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    }

    const otherUser = conv.participants?.find((p) => p._id !== user.id);
    const name = `${otherUser?.first_name} ${otherUser?.last_name}`;
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getConversationInfo = (conversation) => {
    if (conversation.type === "group") {
      return {
        name: conversation.group?.name || "Group",
        picture: conversation.group?.picture,
        isOnline: false,
      };
    }

    const otherUser = conversation.participants?.find((p) => p._id !== user.id);
    return {
      name: `${otherUser?.first_name || ""} ${otherUser?.last_name || ""}`,
      picture: otherUser?.picture,
      isOnline: onlineUsers.includes(otherUser?._id),
      username: otherUser?.username,
    };
  };

  const getLastMessagePreview = (conversation) => {
    const lastMessage = conversation.lastMessage;
    if (!lastMessage) return "No messages yet";

    if (typingUsers[conversation._id]) {
      return <span className="typing_indicator">{typingUsers[conversation._id]} is typing...</span>;
    }

    const isMine = lastMessage.sender?._id === user.id;
    const prefix = isMine ? "You: " : "";

    switch (lastMessage.type) {
      case "image":
        return `${prefix}📷 Photo`;
      case "video":
        return `${prefix}🎥 Video`;
      case "audio":
        return `${prefix}🎵 Audio`;
      case "document":
        return `${prefix}📄 Document`;
      case "location":
        return `${prefix}📍 Location`;
      case "system":
        return getSystemMessageText(lastMessage);
      default:
        return `${prefix}${lastMessage.text?.substring(0, 30) || ""}${
          lastMessage.text?.length > 30 ? "..." : ""
        }`;
    }
  };

  const getSystemMessageText = (message) => {
    const action = message.systemMessage?.action;
    switch (action) {
      case "created_group":
        return "Group created";
      case "added_member":
        return "Member added";
      case "removed_member":
        return "Member removed";
      case "left_group":
        return "Left the group";
      default:
        return "System message";
    }
  };

  return (
    <div className={`chat_sidebar ${visible ? "visible" : "hidden"}`}>
      <div className="chat_sidebar_header">
        <h2>{activeTab === "chats" ? "Conversas" : "Chamadas"}</h2>
        <div className="chat_sidebar_actions">
          <button
            className="conference_btn"
            title="Conferência"
            onClick={onOpenConference}
          >
            <ConferenceIcon />
          </button>
          <div className="menu_container" ref={menuRef}>
            <button
              className="menu_btn"
              title="Menu"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreIcon />
            </button>
            {showMenu && (
              <div className="sidebar_menu">
                <button
                  className="menu_item"
                  onClick={() => {
                    navigate("/chat/new-group");
                    setShowMenu(false);
                  }}
                >
                  <GroupIcon />
                  <span>Novo grupo</span>
                </button>
                <button
                  className="menu_item"
                  onClick={() => {
                    navigate("/search");
                    setShowMenu(false);
                  }}
                >
                  <SearchIcon />
                  <span>Procurar amigos</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="chat_sidebar_tabs">
        <button
          className={`sidebar_tab ${activeTab === "chats" ? "active" : ""}`}
          onClick={() => setActiveTab("chats")}
        >
          <i className="fa fa-comment"></i>
          Conversas
        </button>
        <button
          className={`sidebar_tab ${activeTab === "calls" ? "active" : ""}`}
          onClick={() => setActiveTab("calls")}
        >
          <PhoneIcon />
          Chamadas
        </button>
      </div>

      {activeTab === "chats" && (
        <>
          <div className="chat_search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Pesquisar conversas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="conversations_list">
        {filteredConversations.length === 0 ? (
          <div className="no_conversations">
            <p>No conversations yet</p>
            <span>Start chatting with your friends!</span>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const info = getConversationInfo(conversation);
            const isActive = activeConversation?._id === conversation._id;
            const hasUnread =
              conversation.unreadCount > 0 || conversation.isMarkedUnread;

            return (
              <div
                key={conversation._id}
                className={`conversation_item ${isActive ? "active" : ""} ${
                  hasUnread ? "unread" : ""
                }`}
                onClick={() => onSelectConversation(conversation)}
              >
                <div className="conversation_avatar">
                  <img
                    src={info.picture}
                    alt={info.name}
                    onError={(e) => {
                      e.target.src =
                        "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                    }}
                  />
                  {info.isOnline && <span className="online_dot"></span>}
                </div>
                <div className="conversation_info">
                  <div className="conversation_header">
                    <span className="conversation_name">{info.name}</span>
                    {conversation.lastMessage && (
                      <span className="conversation_time">
                        <Moment fromNow interval={60000}>
                          {conversation.lastMessage.createdAt}
                        </Moment>
                      </span>
                    )}
                  </div>
                  <div className="conversation_preview">
                    <span className="last_message">
                      {getLastMessagePreview(conversation)}
                    </span>
                    {hasUnread && (
                      <span className="unread_badge">
                        {conversation.unreadCount || ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
          </div>
        </>
      )}

      {activeTab === "calls" && (
        <CallHistory user={user} onCallUser={onCallUser} />
      )}
    </div>
  );
}
